import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Baseline,
  Bold,
  ChevronDown,
  ImagePlus,
  IndentDecrease,
  IndentIncrease,
  Italic,
  Link2,
  List,
  ListOrdered,
  Quote,
  Redo2,
  RemoveFormatting,
  Strikethrough,
  Type,
  Underline,
  Undo2,
} from 'lucide-react'
import { useEffect, useId, useRef, useState, type ClipboardEvent, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { promptModal } from '@/components/ui/modal'
import { cn } from '@/lib/utils'
import { anonymizeImages, looksLikeHtmlSource } from '../mailCompose'

type Props = {
  value: string
  onChange: (html: string) => void
  minHeight?: string
  className?: string
  'aria-label'?: string
  id?: string
  /** İmza gibi alanlarda logo/görsel eklemeyi açar (gönderimde maile gömülür). */
  allowImages?: boolean
  /**
   * Düz metin olarak yapıştırılan HTML kodunu (ör. VS Code'dan kopyalanan imza) kod olarak değil, biçimli içerik
   * olarak ekler. İmza alanı için.
   */
  pasteHtmlSource?: boolean
}

/** Gömülü imza görselinin üst sınırı; büyük görseller her maili şişirir. */
const MAX_IMAGE_BYTES = 300 * 1024
const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp']

/** execCommand fontSize ölçeği (1–7); e-posta istemcileri <font size> etiketini tanır. */
const SIZES = [
  { key: 'small', value: '2', className: 'text-xs' },
  { key: 'normal', value: '3', className: 'text-sm' },
  { key: 'large', value: '5', className: 'text-lg' },
  { key: 'huge', value: '6', className: 'text-2xl' },
] as const

/**
 * E-posta istemcilerinde (Gmail, Outlook, Apple Mail) güvenle görünen yazı tipleri; alıcıda olmayan bir yazı tipi
 * seçilirse mail onda farklı görünür. Değer execCommand('fontName') ile <font face> olarak yazılır.
 */
const FONTS = [
  { label: 'Arial', value: 'Arial, Helvetica, sans-serif' },
  { label: 'Helvetica', value: 'Helvetica, Arial, sans-serif' },
  { label: 'Verdana', value: 'Verdana, Geneva, sans-serif' },
  { label: 'Tahoma', value: 'Tahoma, Verdana, sans-serif' },
  { label: 'Trebuchet MS', value: '"Trebuchet MS", Helvetica, sans-serif' },
  { label: 'Calibri', value: 'Calibri, Carlito, Arial, sans-serif' },
  { label: 'Georgia', value: 'Georgia, serif' },
  { label: 'Times New Roman', value: '"Times New Roman", Times, serif' },
  { label: 'Garamond', value: 'Garamond, Georgia, serif' },
  { label: 'Courier New', value: '"Courier New", Courier, monospace' },
] as const

/** Seçimdeki yazı tipinin listedeki adı (tarayıcı "Arial, Helvetica, sans-serif" ya da tırnaklı ad döner). */
function fontLabelOf(raw: string) {
  const first = raw.split(',')[0]?.replace(/["']/g, '').trim().toLowerCase() ?? ''
  return FONTS.find((f) => f.label.toLowerCase() === first)?.label ?? ''
}

const COLORS = ['#111827', '#6b7280', '#dc2626', '#ea580c', '#ca8a04', '#16a34a', '#0284c7', '#2563eb', '#7c3aed', '#db2777']

export function MailRichEditor({ value, onChange, minHeight = '12rem', className, id, allowImages, pasteHtmlSource, 'aria-label': ariaLabel }: Props) {
  const { t } = useTranslation()
  const editorRef = useRef<HTMLDivElement>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  // null: henüz DOM'a yazılmadı. İlk değerle başlatılırsa açılışta içerik basılmaz ve editör boş görünür.
  const lastEmitted = useRef<string | null>(null)
  // Menüler (boyut, renk) odağı editörden alır; komuttan önce son seçim geri yüklenir.
  const savedRange = useRef<Range | null>(null)
  const [font, setFont] = useState('')
  const autoId = useId()
  const editorId = id ?? autoId

  useEffect(() => {
    const el = editorRef.current
    if (!el) return
    if (value === lastEmitted.current) return
    if (el.innerHTML === value) return
    el.innerHTML = value || '<p><br/></p>'
    anonymizeImages(el)
    lastEmitted.current = el.innerHTML
  }, [value])

  function emit() {
    const el = editorRef.current
    if (!el) return
    anonymizeImages(el)
    const html = el.innerHTML
    lastEmitted.current = html
    onChange(html)
  }

  function saveSelection() {
    const sel = window.getSelection()
    const el = editorRef.current
    if (!sel || sel.rangeCount === 0 || !el) return
    const range = sel.getRangeAt(0)
    if (!el.contains(range.commonAncestorContainer)) return
    savedRange.current = range.cloneRange()
    // Araç çubuğu imlecin bulunduğu yerin yazı tipini gösterir (listede yoksa "Varsayılan").
    setFont(fontLabelOf(document.queryCommandValue('fontName')))
  }

  function restoreSelection() {
    const el = editorRef.current
    if (!el) return
    el.focus()
    const range = savedRange.current
    const sel = window.getSelection()
    if (range && sel && el.contains(range.commonAncestorContainer)) {
      sel.removeAllRanges()
      sel.addRange(range)
    }
  }

  function tool(command: string, value?: string) {
    restoreSelection()
    document.execCommand(command, false, value)
    saveSelection()
    emit()
  }

  function insertLink() {
    saveSelection()
    void (async () => {
      const url = await promptModal({
        title: t('mail.mailbox.editor.linkPrompt'),
        placeholder: 'https://',
        inputType: 'url',
        confirmLabel: t('common.confirm'),
      })
      if (!url?.trim()) return
      tool('createLink', url.trim())
    })()
  }

  function insertImage(file: File | undefined) {
    if (!file) return
    if (!IMAGE_TYPES.includes(file.type)) {
      toast.error(t('mail.mailbox.editor.imageType'))
      return
    }
    if (file.size > MAX_IMAGE_BYTES) {
      toast.error(t('mail.mailbox.editor.imageTooBig'))
      return
    }
    const reader = new FileReader()
    reader.onload = () => {
      if (typeof reader.result === 'string') tool('insertImage', reader.result)
    }
    reader.readAsDataURL(file)
  }

  function onPaste(e: ClipboardEvent<HTMLDivElement>) {
    if (!pasteHtmlSource) return
    const text = e.clipboardData.getData('text/plain')
    if (!looksLikeHtmlSource(text)) return
    e.preventDefault()
    tool('insertHTML', text.trim())
    toast.success(t('mail.mailbox.editor.htmlPasted'))
  }

  return (
    <div className={cn('overflow-hidden rounded-lg border border-input bg-card shadow-xs', className)}>
      <div
        className="flex flex-wrap items-center gap-0.5 border-b border-border bg-muted/40 px-1.5 py-1"
        role="toolbar"
        aria-label={t('mail.mailbox.editor.toolbar')}
      >
        <ToolButton label={t('mail.mailbox.editor.undo')} onClick={() => tool('undo')}>
          <Undo2 />
        </ToolButton>
        <ToolButton label={t('mail.mailbox.editor.redo')} onClick={() => tool('redo')}>
          <Redo2 />
        </ToolButton>
        <Divider />
        <DropdownMenu onOpenChange={(open) => open && saveSelection()}>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 w-32 justify-between gap-1 px-2 text-xs font-normal text-muted-foreground"
              aria-label={t('mail.mailbox.editor.fontFamily')}
              title={t('mail.mailbox.editor.fontFamily')}
            >
              <span className="truncate">{font || t('mail.mailbox.editor.fontDefault')}</span>
              <ChevronDown className="size-3.5 shrink-0" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="max-h-72 overflow-y-auto" onCloseAutoFocus={(e) => e.preventDefault()}>
            {FONTS.map((f) => (
              <DropdownMenuItem
                key={f.label}
                style={{ fontFamily: f.value }}
                className={cn(font === f.label && 'bg-muted font-medium')}
                onSelect={() => {
                  tool('fontName', f.value)
                  setFont(f.label)
                }}
              >
                {f.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <DropdownMenu onOpenChange={(open) => open && saveSelection()}>
          <DropdownMenuTrigger asChild>
            <Button type="button" variant="ghost" size="icon" className="size-8 text-muted-foreground" aria-label={t('mail.mailbox.editor.fontSize')} title={t('mail.mailbox.editor.fontSize')}>
              <Type />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" onCloseAutoFocus={(e) => e.preventDefault()}>
            {SIZES.map((s) => (
              <DropdownMenuItem key={s.key} className={s.className} onSelect={() => tool('fontSize', s.value)}>
                {t(`mail.mailbox.editor.sizes.${s.key}`)}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <ToolButton label={t('mail.mailbox.editor.bold')} onClick={() => tool('bold')}>
          <Bold />
        </ToolButton>
        <ToolButton label={t('mail.mailbox.editor.italic')} onClick={() => tool('italic')}>
          <Italic />
        </ToolButton>
        <ToolButton label={t('mail.mailbox.editor.underline')} onClick={() => tool('underline')}>
          <Underline />
        </ToolButton>
        <ToolButton label={t('mail.mailbox.editor.strike')} onClick={() => tool('strikeThrough')}>
          <Strikethrough />
        </ToolButton>
        <DropdownMenu onOpenChange={(open) => open && saveSelection()}>
          <DropdownMenuTrigger asChild>
            <Button type="button" variant="ghost" size="icon" className="size-8 text-muted-foreground" aria-label={t('mail.mailbox.editor.color')} title={t('mail.mailbox.editor.color')}>
              <Baseline />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="grid grid-cols-5 gap-1 p-2" onCloseAutoFocus={(e) => e.preventDefault()}>
            {COLORS.map((color) => (
              <DropdownMenuItem key={color} aria-label={color} className="size-7 justify-center rounded-md p-0" onSelect={() => tool('foreColor', color)}>
                <span className="size-5 rounded-full border border-black/10" style={{ backgroundColor: color }} />
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <Divider />
        <ToolButton label={t('mail.mailbox.editor.alignLeft')} onClick={() => tool('justifyLeft')}>
          <AlignLeft />
        </ToolButton>
        <ToolButton label={t('mail.mailbox.editor.alignCenter')} onClick={() => tool('justifyCenter')}>
          <AlignCenter />
        </ToolButton>
        <ToolButton label={t('mail.mailbox.editor.alignRight')} onClick={() => tool('justifyRight')}>
          <AlignRight />
        </ToolButton>
        <Divider />
        <ToolButton label={t('mail.mailbox.editor.bulletList')} onClick={() => tool('insertUnorderedList')}>
          <List />
        </ToolButton>
        <ToolButton label={t('mail.mailbox.editor.numberedList')} onClick={() => tool('insertOrderedList')}>
          <ListOrdered />
        </ToolButton>
        <ToolButton label={t('mail.mailbox.editor.outdent')} onClick={() => tool('outdent')}>
          <IndentDecrease />
        </ToolButton>
        <ToolButton label={t('mail.mailbox.editor.indent')} onClick={() => tool('indent')}>
          <IndentIncrease />
        </ToolButton>
        <ToolButton label={t('mail.mailbox.editor.quote')} onClick={() => tool('formatBlock', 'blockquote')}>
          <Quote />
        </ToolButton>
        <Divider />
        <ToolButton label={t('mail.mailbox.editor.link')} onClick={insertLink}>
          <Link2 />
        </ToolButton>
        {allowImages ? (
          <>
            <ToolButton label={t('mail.mailbox.editor.image')} onClick={() => fileRef.current?.click()}>
              <ImagePlus />
            </ToolButton>
            <input
              ref={fileRef}
              type="file"
              accept={IMAGE_TYPES.join(',')}
              className="sr-only"
              tabIndex={-1}
              onChange={(e) => {
                insertImage(e.target.files?.[0])
                e.target.value = ''
              }}
            />
          </>
        ) : null}
        <ToolButton label={t('mail.mailbox.editor.clear')} onClick={() => tool('removeFormat')}>
          <RemoveFormatting />
        </ToolButton>
      </div>
      <div
        id={editorId}
        ref={editorRef}
        role="textbox"
        aria-multiline
        aria-label={ariaLabel ?? t('mail.mailbox.body')}
        contentEditable
        suppressContentEditableWarning
        className="max-w-none overflow-y-auto scrollbar-none px-3.5 py-3 text-sm leading-relaxed text-foreground outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/30 [&_a]:text-primary [&_a]:underline [&_blockquote]:my-2 [&_blockquote]:border-l-2 [&_blockquote]:border-border [&_blockquote]:pl-3 [&_blockquote]:text-muted-foreground [&_img]:inline-block [&_img]:max-w-full [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:my-1.5 [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5"
        style={{ minHeight }}
        onInput={emit}
        onBlur={() => {
          saveSelection()
          emit()
        }}
        onKeyUp={saveSelection}
        onMouseUp={saveSelection}
        onPaste={onPaste}
        onKeyDown={(e) => {
          if (!(e.metaKey || e.ctrlKey)) return
          const key = e.key.toLowerCase()
          const command = key === 'b' ? 'bold' : key === 'i' ? 'italic' : key === 'u' ? 'underline' : null
          if (command) {
            e.preventDefault()
            tool(command)
          }
        }}
      />
    </div>
  )
}

function Divider() {
  return <span className="mx-1 h-4 w-px bg-border" aria-hidden />
}

function ToolButton({ label, onClick, children }: { label: string; onClick: () => void; children: ReactNode }) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="size-8 text-muted-foreground"
      aria-label={label}
      title={label}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
    >
      {children}
    </Button>
  )
}
